import React, { useEffect, useRef, useState } from "react"
import { motion } from "motion/react"
import { Link, useNavigate } from "react-router-dom"
import { CinemaPicker } from "../../features/location"
import PostersBackground from "../../features/start/PostersBackground"
import { appStore } from "../../store/appStore"
import { userStore } from "../../store/userStore"
import AppStoreBadges from "../../components/AppStoreBadges"
import { hrefForCurrentCinema } from "../../lib/cinemaSlugs"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

const CHAIR_VIDEO_LOOP_WEBM = "/video/mk-chair-loop.webm"
const CHAIR_VIDEO_LOOP_HEVC = "/video/mk-chair-loop-hevc.mov"
const CHAIR_VIDEO_LOOP_MP4 = "/video/mk-chair-loop.mp4"
const CHAIR_POSTER_SRC = "/video/mk-chair-poster.png"

function usePrefersReducedMotion() {
    const [reduceMotion, setReduceMotion] = useState(false)

    useEffect(() => {
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
        const sync = () => setReduceMotion(mq.matches)
        sync()
        mq.addEventListener("change", sync)
        return () => mq.removeEventListener("change", sync)
    }, [])

    return reduceMotion
}

/* ─── Sticker ─────────────────────────────────────────────────────────────── */
function Sticker({ label, color, rotate = 0, className = "" }: { label: string; color: string; rotate?: number; className?: string }) {
    return (
        <span
            className={`inline-flex w-fit max-w-full items-center px-3 py-1 rounded-full border text-[11px] font-bold uppercase tracking-widest whitespace-nowrap select-none ${className}`}
            style={{
                fontFamily: MONO,
                color,
                borderColor: `${color}66`,
                background: `${color}22`,
                transform: `rotate(${rotate}deg)`,
            }}>
            {label}
        </span>
    )
}

/* ─── Section wrapper ─────────────────────────────────────────────────────── */
function Section({ children, className = "" }: { children: React.ReactNode; className?: string }) {
    return (
        <div className={`max-w-[1240px] mx-auto px-4 sm:px-8 w-full ${className}`}>
            {children}
        </div>
    )
}

/* ─── Hero ────────────────────────────────────────────────────────────────── */
function Hero({ onScrollToCinemas }: { onScrollToCinemas: () => void }) {
    const navigate = useNavigate()
    const { user } = userStore()
    const { locationSelected } = appStore()
    const isAuthed = Boolean(user?.token)

    const primaryBtnClass =
        "flex w-full items-center justify-center gap-2 rounded-full px-7 py-3.5 text-white font-bold sm:w-auto"
    const primaryBtnStyle = {
        fontFamily: MONO,
        background: "#e4206b",
        boxShadow: "0 0 28px rgba(228,32,107,0.45)",
        fontSize: 14,
        letterSpacing: "0.04em",
    } as const

    return (
        <section className="relative w-full overflow-hidden">
            <PostersBackground />
            <div
                className="pointer-events-none absolute inset-0 z-[1]"
                style={{
                    background:
                        "linear-gradient(to right, rgba(22,40,74,0.97) 0%, rgba(22,40,74,0.82) 32%, rgba(22,40,74,0.35) 48%, transparent 58%)",
                }}
            />
            <Section className="relative z-10 py-16 sm:py-24">
                <div className="flex max-w-[700px] flex-col gap-8">
                    <p className="sr-only">
                        Мягкий кинотеатр — роботизированные кресла, больше места, 4K Dolby.
                        Роботизированные кресла, которые откидываются под фильм.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <Sticker label="роботизированные кресла" color="#29cc6a" rotate={-3} />
                        <Sticker label="больше места" color="#349be6" rotate={2} />
                        <Sticker label="4K Dolby" color="#e4206b" rotate={-1} />
                    </div>

                    <motion.h1
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                        className="text-white leading-[1.05]"
                        style={{
                            fontFamily: DISPLAY,
                            fontWeight: 800,
                            fontSize: "clamp(36px, 7vw, 80px)",
                            letterSpacing: "-0.03em",
                        }}>
                        Мягкий{" "}
                        <span style={{ color: "#349be6" }}>кинотеатр</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
                        className="text-[17px] sm:text-[20px] leading-relaxed"
                        style={{ fontFamily: BODY, fontWeight: 500, color: "rgba(244,246,255,0.75)", maxWidth: 520 }}>
                        Роботизированные кресла, которые откидываются под фильм
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                        className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                        {isAuthed ? (
                            locationSelected ? (
                                <>
                                    <motion.button
                                        type="button"
                                        whileHover={{ scale: 1.04, rotate: -1 }}
                                        whileTap={{ scale: 0.97 }}
                                        onClick={() => navigate(hrefForCurrentCinema("/afisha"))}
                                        className={primaryBtnClass}
                                        style={primaryBtnStyle}>
                                        К афише!
                                    </motion.button>
                                    <motion.button
                                        type="button"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={onScrollToCinemas}
                                        className="flex w-full items-center justify-center rounded-full border border-white/12 bg-white/[0.04] px-6 py-3.5 text-[14px] text-white/55 transition-colors hover:border-white/20 hover:bg-white/[0.07] hover:text-white/75 sm:w-auto"
                                        style={{ fontFamily: MONO, fontWeight: 700, letterSpacing: "0.04em" }}>
                                        Сменить кинотеатр
                                    </motion.button>
                                </>
                            ) : (
                                <motion.button
                                    type="button"
                                    whileHover={{ scale: 1.04, rotate: -1 }}
                                    whileTap={{ scale: 0.97 }}
                                    onClick={onScrollToCinemas}
                                    className={primaryBtnClass}
                                    style={primaryBtnStyle}>
                                    Выбрать кинотеатр →
                                </motion.button>
                            )
                        ) : (
                            /* Город неизвестен — только выбор кинотеатра, вход появится после */
                            <motion.button
                                type="button"
                                whileHover={{ scale: 1.04, rotate: -1 }}
                                whileTap={{ scale: 0.97 }}
                                onClick={onScrollToCinemas}
                                className={primaryBtnClass}
                                style={primaryBtnStyle}>
                                Выбрать кинотеатр →
                            </motion.button>
                        )}
                    </motion.div>
                </div>
            </Section>
        </section>
    )
}

/* ─── Soft chair product ──────────────────────────────────────────────────── */
function SoftChairMedia() {
    const videoRef = useRef<HTMLVideoElement>(null)
    const reduceMotion = usePrefersReducedMotion()

    useEffect(() => {
        const video = videoRef.current
        if (!video || reduceMotion) return
        video.playbackRate = 0.85
        const play = () => {
            void video.play().catch(() => {})
        }
        play()
        video.addEventListener("loadeddata", play)
        return () => video.removeEventListener("loadeddata", play)
    }, [reduceMotion])

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.55 }}
            className="relative flex aspect-[16/10] w-full items-center justify-center overflow-visible bg-transparent">
            {reduceMotion ? (
                <img
                    src={CHAIR_POSTER_SRC}
                    alt="Роботизированное кресло мягкого кинотеатра"
                    className="max-h-full w-full object-contain object-center"
                    style={{ transform: "scaleX(-1)" }}
                />
            ) : (
                <video
                    ref={videoRef}
                    className="max-h-full w-full bg-transparent object-contain object-center"
                    style={{ transform: "scaleX(-1)" }}
                    poster={CHAIR_POSTER_SRC}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-label="Роботизированное кресло мягкого кинотеатра">
                    {/* Порядок важен: Safari (iOS/macOS) играет webm без альфы (серая подложка),
                        поэтому первым идёт HEVC with Alpha — Chrome/Firefox его пропустят
                        (не знают video/quicktime) и возьмут webm с альфой */}
                    <source src={CHAIR_VIDEO_LOOP_HEVC} type='video/quicktime; codecs="hvc1"' />
                    <source src={CHAIR_VIDEO_LOOP_WEBM} type="video/webm" />
                    <source src={CHAIR_VIDEO_LOOP_MP4} type="video/mp4" />
                </video>
            )}
        </motion.div>
    )
}

function SoftChair() {
    return (
        <Section className="py-16 sm:py-20">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-14">
                <div className="flex flex-col items-start gap-6">
                    <Sticker label="мягкие кресла" color="#29cc6a" rotate={-2} />
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.45 }}
                        className="text-white"
                        style={{
                            fontFamily: DISPLAY,
                            fontWeight: 800,
                            fontSize: "clamp(28px, 5vw, 52px)",
                            letterSpacing: "-0.02em",
                            lineHeight: 1.1,
                        }}>
                        Мягкие кресла в{" "}
                        <span style={{ color: "#349be6" }}>каждом зале</span>
                    </motion.h2>
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.45, delay: 0.08 }}
                        className="flex flex-col gap-4 text-[16px] sm:text-[18px] leading-relaxed"
                        style={{ fontFamily: BODY, fontWeight: 500, color: "rgba(255,255,255,0.65)", maxWidth: 520 }}>
                        <p>
                            Потрясающие роботизированные кресла в каждом зале, больше места между рядами,
                            качественная картинка и звук — это всё про нас!
                        </p>
                        <p>
                            Угол раскладывания кресел зависит от ряда и максимален на первом ряду.
                        </p>
                    </motion.div>
                </div>

                <SoftChairMedia />
            </div>
        </Section>
    )
}

/* ─── App download ────────────────────────────────────────────────────────── */
function OurApp() {
    return (
        <Section className="py-16 sm:py-20">
            <div className="rounded-[36px] border-2 border-white/10 bg-gradient-to-br from-white/[0.06] to-transparent p-7 sm:p-12 flex flex-col lg:flex-row gap-10 items-start lg:items-center">
                <div className="flex flex-col gap-6 flex-1">
                    <div className="flex flex-wrap gap-2.5">
                        <Sticker label="iOS" color="#29cc6a" rotate={-3} />
                        <Sticker label="Android" color="#349be6" rotate={2} />
                        <Sticker label="RuStore" color="#e4206b" rotate={-2} />
                    </div>

                    <h2
                        className="text-white"
                        style={{
                            fontFamily: DISPLAY,
                            fontWeight: 800,
                            fontSize: "clamp(26px, 4vw, 48px)",
                            letterSpacing: "-0.02em",
                        }}>
                        Наше{" "}
                        <span style={{ color: "#29cc6a" }}>приложение</span>
                    </h2>

                    <p
                        className="text-[15px] sm:text-[17px] leading-relaxed"
                        style={{ fontFamily: BODY, fontWeight: 500, color: "rgba(255,255,255,0.7)", maxWidth: 460 }}>
                        Копи и трать кинобонусы, покупай билеты и проходи в зал без очередей,
                        пиши отзывы о фильмах и будь в курсе всех новинок проката.
                    </p>

                    <AppStoreBadges />

                    <p className="text-[12px] text-white/35 max-w-[440px] leading-relaxed" style={{ fontFamily: MONO }}>
                        Авторизуясь в приложении, вы соглашаетесь с{" "}
                        <Link
                            to="/privacy"
                            className="underline text-white/55 hover:text-white transition-colors">
                            политикой конфиденциальности
                        </Link>{" "}
                        и{" "}
                        <Link to="/terms" className="underline text-white/55 hover:text-white transition-colors">
                            пользовательским соглашением
                        </Link>
                        .
                    </p>
                </div>

                <div className="flex-shrink-0 flex items-center justify-center w-full lg:w-auto">
                    <div className="flex gap-4 items-end">
                        {[
                            { emoji: "🎬", color: "#e4206b", size: 64, rotate: -8 },
                            { emoji: "🎟", color: "#349be6", size: 80, rotate: 4 },
                            { emoji: "🛋", color: "#29cc6a", size: 64, rotate: -4 },
                        ].map((item, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                whileHover={{ y: -8, rotate: item.rotate * 1.5 }}
                                className="flex items-center justify-center rounded-[20px] border border-white/10 bg-white/[0.06]"
                                style={{
                                    width: item.size + 20,
                                    height: item.size + 20,
                                    fontSize: item.size * 0.7,
                                    rotate: `${item.rotate}deg`,
                                    boxShadow: `0 0 24px ${item.color}33`,
                                }}>
                                {item.emoji}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </Section>
    )
}

/* ─── Footer strip ────────────────────────────────────────────────────────── */
function FooterStrip() {
    const year = new Date().getFullYear()

    return (
        <div style={{ background: "rgba(52,155,230,0.1)", borderTop: "1px solid rgba(52,155,230,0.2)" }}>
            <Section className="py-8">
                <div
                    className="flex flex-col gap-1.5 text-center sm:text-left max-w-[560px] text-[13px] text-white/35 leading-relaxed"
                    style={{ fontFamily: BODY, fontWeight: 500 }}>
                    <p>© {year} ООО Империя игр</p>
                    <p>
                        Все материалы сайта доступны по лицензии{" "}
                        <a
                            href="https://creativecommons.org/licenses/by/4.0/"
                            target="_blank"
                            rel="noreferrer"
                            className="text-white/55 underline underline-offset-2 transition-colors hover:text-white/80">
                            Creative Commons Attribution 4.0 International
                        </a>
                    </p>
                </div>
            </Section>
        </div>
    )
}

/* ─── Start page ──────────────────────────────────────────────────────────── */
export default function Start() {
    const cinemasRef = useRef<HTMLDivElement>(null)

    const handleScrollToCinemas = () => {
        cinemasRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }

    return (
        <div className="w-full overflow-x-hidden pb-4">
            <Hero onScrollToCinemas={handleScrollToCinemas} />
            <SoftChair />
            <CinemaPicker sectionRef={cinemasRef} variant="embedded" />
            <OurApp />
            <FooterStrip />
        </div>
    )
}
